import z from 'zod';
import { partySchema } from '../../common';
import { dateSchema } from '../../validation';

/**
 * What kind of event a ticket timeline row represents. Append-only —
 * a later edit never rewrites an earlier row.
 */
export const ticketTimelineTypeSchema = z.enum([
  'created',
  'status_changed',
  'priority_changed',
  'assigned',
  'reply_sent',
  'note_added',
  'reopened',
  /** The `ticket_closed` WhatsApp template was queued for the requester. */
  'requester_notified',
]);
export type TicketTimelineType = z.infer<typeof ticketTimelineTypeSchema>;

/**
 * A single row on `Ticket.timeline[]`.
 *
 * - `at`    — when it happened.
 * - `actor` — who did it. Omitted for system events (the AI opening a
 *   ticket on the requester's behalf stamps the requester, not a staff
 *   member, so "who raised this" stays honest).
 * - `data`  — free-form payload:
 *   - `status_changed`   → `{ from, to }`
 *   - `priority_changed` → `{ from, to }`
 *   - `assigned`         → `{ from?, to }`
 *   - `note_added` / `reply_sent` → `{ body }`
 *   - `requester_notified` → `{ template, phone, delivery, note? }`
 */
export const ticketTimelineSchema = z.object({
  at: dateSchema,
  actor: partySchema.optional(),
  type: ticketTimelineTypeSchema,
  data: z.record(z.unknown()).optional(),
});
export type TicketTimeline = z.infer<typeof ticketTimelineSchema>;

/**
 * A message on the ticket thread.
 *
 * `internal` notes are staff-only working notes; everything else is
 * correspondence with the requester. They live in one array because a
 * support thread reads as one chronological story — splitting them into
 * two collections is how "we already answered that" gets lost.
 */
export const ticketNoteSchema = z.object({
  at: dateSchema,
  author: partySchema.optional(),
  body: z.string().trim().min(1),
  internal: z.boolean().default(false),
});
export type TicketNote = z.infer<typeof ticketNoteSchema>;
